import React, { useState, useEffect } from "react";
import { Eye, EyeOff } from "lucide-react";
import Monogram from "./Monogram";

// Site-wide access gate. The password is intentionally simple (guest-friendly),
// designed to gently keep casual browsers away — not a security wall.
// Guests who type the password once are remembered on their device.
const SITE_PASSWORD = "anitem2027";
const STORAGE_KEY = "am_site_access_v1";

// Normalize input so common paste/autofill/keyboard quirks don't block guests:
// strip zero-width characters, all whitespace (incl. NBSP), and lowercase.
const normalize = (s = "") =>
  s
    .replace(/[\u200B-\u200D\uFEFF]/g, "") // zero-width chars
    .replace(/\s+/g, "") // any whitespace (spaces, NBSP, tabs)
    .toLowerCase();

const SiteGate = ({ children }) => {
  const [unlocked, setUnlocked] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) === "granted"; }
    catch { return false; }
  });
  const [value, setValue] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [reveal, setReveal] = useState(false);

  useEffect(() => {
    if (!unlocked) document.title = "With Love · Private";
    else document.title = "Anjola & Michael · With Love";
  }, [unlocked]);

  const submit = (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    setTimeout(() => {
      if (normalize(value) === normalize(SITE_PASSWORD)) {
        try { localStorage.setItem(STORAGE_KEY, "granted"); } catch {}
        setUnlocked(true);
      } else {
        setError("That doesn't match. Please check the password from your invitation (no spaces, all lowercase).");
      }
      setBusy(false);
    }, 300);
  };

  if (unlocked) return children;

  return (
    <div data-testid="site-gate" className="min-h-screen relative grain flex items-center justify-center px-6 py-16">
      <div className="max-w-md w-full text-center relative z-[2]">
        <Monogram className="h-24 w-auto mx-auto" />
        <div className="mt-6 text-[10px] tracking-[0.4em] uppercase text-gold">A Private Celebration</div>
        <h1 className="mt-3 font-serif text-5xl sm:text-6xl leading-[0.95] tracking-tighter text-ink">
          Anjola<span className="italic text-gold font-light"> &amp; </span>Michael
        </h1>
        <p className="mt-6 text-sm sm:text-base text-ink-soft leading-relaxed max-w-sm mx-auto">
          This site is reserved for our cherished guests. Please enter the password shared on your invitation to continue.
        </p>

        <form onSubmit={submit} className="mt-10 space-y-6" data-testid="site-gate-form" noValidate>
          <div className="relative">
            <input
              data-testid="site-gate-input"
              type={reveal ? "text" : "password"}
              autoFocus
              autoComplete="off"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              inputMode="text"
              name="access-code"
              value={value}
              onChange={(e) => { setValue(e.target.value); if (error) setError(""); }}
              placeholder="Password"
              className="w-full bg-transparent border-0 border-b border-hairline text-center text-lg py-3 pr-10 text-ink placeholder:text-ink-faint focus:outline-none focus:border-gold transition-colors tracking-[0.15em]"
            />
            <button
              type="button"
              onClick={() => setReveal((r) => !r)}
              data-testid="site-gate-reveal"
              aria-label={reveal ? "Hide password" : "Show password"}
              className="absolute right-1 top-1/2 -translate-y-1/2 p-2 text-ink-faint hover:text-gold transition-colors"
            >
              {reveal ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {error && <div data-testid="site-gate-error" className="text-xs text-burgundy tracking-wide">{error}</div>}
          <button
            type="submit"
            disabled={busy || !value.trim()}
            data-testid="site-gate-submit"
            className="btn-gold w-full inline-flex items-center justify-center border border-gold text-gold text-[11px] tracking-[0.25em] uppercase px-10 py-4 rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {busy ? "Opening…" : "Enter"}
          </button>
        </form>

        <div className="mt-12 text-[10px] tracking-[0.3em] uppercase text-ink-faint">
          With Love · 05 · 08 · 2027
        </div>
      </div>
    </div>
  );
};

export default SiteGate;
